import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LOGO_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Receipt, TicketCheck, ArrowRight, LogOut } from "lucide-react";

const MODULES = [
  {
    id: "leistung",
    icon: Receipt,
    title: "Leistungsabrechnung & Aufmaß",
    desc: "Aufträge erfassen, Aufmaß verwalten, Mitarbeiterabrechnung, Preislisten, PDF-Berichte, KI-Import.",
    to: "/",
    allowedRoles: ["admin", "innendienst", "buchhaltung"],
  },
  {
    id: "ticket",
    icon: TicketCheck,
    title: "Ticket Disposition",
    desc: "GWV-Tickets, Kundenkontakt, Terminierung, Partnerfirmen, Kapazitäten, Abwesenheiten.",
    to: "/ticket",
    allowedRoles: ["admin", "innendienst", "disponent", "techniker"],
  },
];

export default function ModuleSelect() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const role = user?.role || "innendienst";
  const visible = MODULES.filter((m) => m.allowedRoles.includes(role) || role === "admin");

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#F1F5F9" }} data-testid="module-select">
      <header className="bg-white border-b" style={{ borderColor: "#E2E8F0" }}>
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="SKALARTECH" className="h-12 w-auto" />
            <div>
              <div className="font-bold tracking-wide">SKALARTECH</div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500">Hauptmenü</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right text-xs leading-tight">
              <div className="font-semibold text-slate-900">{user?.name}</div>
              <div className="uppercase tracking-wider text-slate-500">{user?.role}</div>
            </div>
            <Button
              variant="outline"
              className="h-10"
              data-testid="module-select-logout"
              onClick={async () => { await logout(); nav("/login"); }}
            >
              <LogOut className="h-4 w-4 mr-2" /> Abmelden
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-5xl">
          <div className="mb-10 text-center">
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0F172A]">Willkommen zurück, {user?.name?.split(" ")[0]}</h1>
            <p className="text-sm text-slate-500 mt-2">Wählen Sie ein Modul, um fortzufahren</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {visible.map((m) => (
              <button
                key={m.id}
                data-testid={`module-card-${m.id}`}
                onClick={() => nav(m.to)}
                className="group bg-white border rounded-lg p-8 text-left hover:border-[#0A2540] transition-all hover:shadow-lg"
                style={{ borderColor: "#E2E8F0" }}
              >
                <div className="w-14 h-14 rounded-md flex items-center justify-center mb-5" style={{ background: "#0A2540" }}>
                  <m.icon className="h-7 w-7 text-white" />
                </div>
                <h2 className="text-xl font-bold text-[#0F172A]">{m.title}</h2>
                <p className="text-sm text-slate-500 mt-2 min-h-[48px]">{m.desc}</p>
                <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#0A2540] group-hover:gap-3 transition-all">
                  Öffnen <ArrowRight className="h-4 w-4" />
                </div>
              </button>
            ))}
          </div>
          {visible.length === 0 && (
            <div className="bg-white border rounded-md p-8 text-center text-slate-500" style={{ borderColor: "#E2E8F0" }}>
              Keine Module für Ihre Rolle freigeschaltet. Bitte kontaktieren Sie den Administrator.
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
